import type { GetEstimateInfoMinimalByIdResponse } from '@massilia/queries'

import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { BedDouble, CalendarDays, Plane } from 'lucide-react'

import { formatPrice } from '@/utils/format-price'

interface TripSummaryProps {
  totalPrice: number
  trip: GetEstimateInfoMinimalByIdResponse['trip']
}

// Les horaires de vol sont en heure locale aéroport avec un faux suffixe « +00:00 » :
// on le coupe, sinon le navigateur convertit et affiche 2 h de trop l'été à Paris.
const local = (iso: string) => parseISO(iso.slice(0, 16))
const day = (iso: string) => format(local(iso), 'd MMMM yyyy', { locale: fr })
const dayTime = (iso: string) => format(local(iso), "d MMM yyyy 'à' HH'h'mm", { locale: fr })

/**
 * Ce que le client s'apprête à réserver, rappelé en tête du formulaire.
 *
 * Il ne l'était nulle part : la page enchaînait « vérifiez vos passagers » puis
 * le prix et le bouton de confirmation, sans jamais dire de quel voyage il
 * s'agissait. On demandait de valider plusieurs milliers d'euros sur la foi du
 * lien cliqué à l'écran précédent.
 */
export function TripSummary({ totalPrice, trip }: TripSummaryProps) {
  const hasContent = trip.route !== '' || trip.segments.length > 0 || trip.hotels.length > 0
  if (!hasContent) return null

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-brand-100 bg-brand-50/50 p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex flex-col gap-0.5">
          {trip.route !== '' && (
            <h2 className="text-lg font-semibold break-words text-gray-light-900">{trip.route}</h2>
          )}
          <p className="text-sm text-gray-light-600">{trip.name}</p>
        </div>
        <p className="text-lg font-semibold whitespace-nowrap text-gray-light-900">
          {formatPrice(totalPrice / 100)}
        </p>
      </div>

      {trip.travelDates.departure && (
        <p className="flex items-start gap-2 text-sm text-gray-light-700">
          <CalendarDays aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            Du {day(trip.travelDates.departure)}
            {trip.travelDates.return && <> au {day(trip.travelDates.return)}</>}
          </span>
        </p>
      )}

      {trip.segments.length > 0 && (
        <ul className="flex flex-col gap-1">
          {trip.segments.map((segment, index) => (
            <li
              className="flex items-start gap-2 text-sm text-gray-light-700"
              key={`${segment.departureAt}-${index}`}
            >
              <Plane aria-hidden className="mt-0.5 size-4 shrink-0" />
              <span>
                <span className="font-medium">
                  {segment.departureLocation} → {segment.arrivalLocation}
                </span>{' '}
                · {dayTime(segment.departureAt)} · {segment.airline}
              </span>
            </li>
          ))}
        </ul>
      )}

      {trip.hotels.map((hotel, index) => (
        <p
          className="flex items-start gap-2 text-sm text-gray-light-700"
          key={`${hotel.name}-${index}`}
        >
          <BedDouble aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            <span className="font-medium">{hotel.name}</span> · {hotel.location} · du{' '}
            {day(hotel.fromDate)} au {day(hotel.toDate)}
          </span>
        </p>
      ))}
    </section>
  )
}
